import React, { useEffect, useState, useRef } from "react";
import "./clientSearchInput.css";
import BdrInput from "../../../../components/BdrInput/BdrInput";
import { CLIENT_SEARCH } from "../../../../utils/GraphQL/queries";
import { useLazyQuery } from "@apollo/client";
import { useNavigate } from "react-router";
import { Popper } from "@mui/material";
import BdrMenu from "../../../../components/BdrMenu/BdrMenu";
import capitalizeFirstLetters from "../../../../utils/formatters/capitalizeFirstLetters";
import phoneNumberFormat from "../../../../utils/formatters/phoneNumberFormat";

const pickPreferredPhone = (phoneNumberIds) => {
  if (!phoneNumberIds?.length) return "";
  const cell = phoneNumberIds.find((p) => p?.numberType === "Cell");
  const chosen = cell || phoneNumberIds[0];
  if (!chosen?.phoneNumber) return "";
  return phoneNumberFormat(chosen.phoneNumber);
};

/* Exported so a host rendering results ITSELF (see `onResults`) labels them
   exactly as this component would. Two copies of this would drift. */
export const buildEmailSecondary = (item) => {
  const phone = pickPreferredPhone(item?.phoneNumberIds);
  const parts = [item?.primaryEmail, phone ? `(${phone})` : null].filter(Boolean);
  return parts.join(" ");
};

/*
 * PHONE SEARCH — the web twin of the server's `phoneSearchPrefix`
 * (server/utils/phoneLink.js). Both are tested against ONE table,
 * server/utils/__tests__/fixtures/phone_search_prefix_cases.json, so they
 * cannot drift: "210388", "(210) 365", "+1 210 365" all → the stored dashed
 * shape "210-365". "" means nothing searchable ("1", "+1", "(").
 */
export const phoneSearchPrefix = (raw) => {
  const digits = String(raw ?? "")
    .replace(/\D/g, "")
    .replace(/^1/, "")
    .slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/* The server's path test: anything not starting with a letter is a PHONE
   search there — "(210) 365" and "+1 210" included — so the display has to
   agree with it, not with the field formatter's "first character is a
   digit". */
export const isPhoneQuery = (text) => {
  const trimmed = String(text ?? "").trim();
  return trimmed.length > 0 && !/^[a-zA-Z]/.test(trimmed) && /\d/.test(trimmed);
};

/*
 * The number a phone search matched on: the SMALLEST stored number that starts
 * with the search's prefix — the same number the server ranked the client by,
 * so the list never looks unsorted. `matched` is the part that matched (bold
 * in the UI), `rest` the remainder. null when nothing matches, or when the
 * prefix is empty (every number "starts with" "").
 */
export const pickMatchedPhone = (phoneNumberIds, text) => {
  const prefix = phoneSearchPrefix(text);
  if (!prefix) return null;
  let best = null;
  for (const p of phoneNumberIds || []) {
    const number = p?.phoneNumber || "";
    if (number.startsWith(prefix) && (!best || number < best.phoneNumber)) best = p;
  }
  if (!best) return null;
  return {
    phone: best,
    matched: best.phoneNumber.slice(0, prefix.length),
    rest: best.phoneNumber.slice(prefix.length),
  };
};

const ClientSearchInput = ({
  /**
   * HAND THE RESULTS TO THE HOST instead of rendering them.
   *
   * Given this, the component renders only the FIELD: no result list, and no
   * keyboard handling. That is the whole point — a host that draws the results
   * (a menu listing them as its own items) must own the arrow keys and Enter
   * too, or the two compete for the same keys and Enter fires on both lists at
   * once.
   *
   * Added for the Clients nav menu, where a floating result list inside a
   * floating menu meant two surfaces, two keyboard models, and results
   * spilling past the panel's edge.
   *
   * Called as `onResults(list, status)`:
   *   list    the rows to draw — [] while a name search is pending, the
   *           on-screen filter of the last answer while a NUMBER search is
   *           pending, and [] after a failed search
   *   status  { query, isNumber, settled, failed, inFlight, retry }
   *           settled  the answer in hand is for exactly this text
   *           failed   that answer was a failure (network / server)
   *           inFlight a request for this text is running right now
   *           retry()  re-run the search for this text now
   * Store the whole status object; never `setState(retry)` (React would call
   * it as an updater). Should be stable (useCallback) — it is an effect
   * dependency.
   */
  onResults,
  /**
   * Focus the field on mount. Opt-in, and the CALLER decides — whether a
   * keyboard appearing is welcome depends entirely on where this is hosted,
   * which this component cannot know.
   */
  autoFocus = false,
  returnResults,
  setWidth,
  menuReturn,
  handleSnack,
  forManagerOfClientId,
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  /*
   * ONE source of truth for results: the server's last answer, written only
   * when a search RETURNS. Everything on screen is derived from it below.
   *
   * There used to be a `clientData` list that the instant number filter
   * rewrote on every keystroke, plus a `callCompleted` flag. After backspacing
   * a number search, the flag said "settled" while the list held the filter's
   * leftovers — "no clients" beside a number seven clients have. Deriving the
   * filter from the answer instead means it can always widen again.
   */
  const [answer, setAnswer] = useState({ query: null, list: [], failed: false });
  /* The text a request is running for right now — the spinner during a
     Retry, which does not change the text. */
  const [inFlightQuery, setInFlightQuery] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [clientSearchCall] = useLazyQuery(CLIENT_SEARCH, {
    fetchPolicy: "network-only",
  });
  const elRef = useRef();
  const listRef = useRef();

  useEffect(() => {
    if (anchorEl) {
      const onScroll = () => {
        blurResults();
      };
      // clean up code
      window.removeEventListener("scroll", onScroll);
      window.addEventListener("scroll", onScroll, { passive: true });
      return () => window.removeEventListener("scroll", onScroll);
    }
    // eslint-disable-next-line
  }, [anchorEl]);

  useEffect(() => {
    if (menuReturn?.anchor) {
      setSearchParams("");
    }
    // eslint-disable-next-line
  }, [menuReturn?.anchor]);

  /*
   * The request in flight, so a newer keystroke (or a Retry, or unmount) can
   * CANCEL it. Without this an answer for "Ann" landing after the field said
   * "Anna" was reported as the answer for "Anna".
   */
  const inFlightRef = useRef(null);

  useEffect(() => () => inFlightRef.current?.abort(), []);

  const runSearch = async (query) => {
    inFlightRef.current?.abort();
    const controller = new AbortController();
    inFlightRef.current = controller;
    setInFlightQuery(query);

    const { data, error } = await clientSearchCall({
      variables: {
        searchParam: query,
        // Manager Linking — when set, server excludes [self + descendants]
        // from results and annotates each result with its existing manager
        // (drives the inline "managed by X" hint in the dropdown).
        ...(forManagerOfClientId
          ? { forManagerOfClientId }
          : {}),
      },
      context: {
        fetchOptions: { signal: controller.signal },
        /* Apollo merges identical in-flight queries by default, so an abort
           here could cancel a request ANOTHER search shares — a Retry, or a
           second search box with the same text. Each search owns its own. */
        queryDeduplication: false,
      },
    });

    /* A cancelled search still RESOLVES (Apollo hands back the previous
       result with the error), so ignore it outright. */
    if (controller.signal.aborted) return;
    inFlightRef.current = null;
    setInFlightQuery(null);

    /* A failure also resolves with the PREVIOUS data spread in, so `data`
       alone cannot tell it from a success — read `error`. On a failure the
       previous list stays (what the search boxes have always shown); the
       Clients menu, told `failed`, shows "Search failed" instead. */
    const failed = Boolean(error) || !data;
    setAnswer((prev) =>
      failed
        ? { query, list: prev.list, failed: true }
        : { query, list: data.searchClients || [], failed: false }
    );
  };

  useEffect(() => {
    inFlightRef.current?.abort();
    inFlightRef.current = null;
    setInFlightQuery(null);

    /* A blank or spaces-only field is not a search (the server used to answer
       it with 7 arbitrary clients). */
    if (!searchParams?.trim()) return undefined;

    const timeout = setTimeout(() => {
      runSearch(searchParams);
    }, 1000);

    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line
  }, [searchParams]);

  /*
   * Everything shown is DERIVED from the answer:
   *   settled  the answer in hand is for exactly this text
   *   shown    settled → that answer; a number still being searched → the
   *            last answer filtered to this number (so it narrows AND widens
   *            as digits are typed and deleted); a name being searched → []
   */
  const query = searchParams?.trim() ? searchParams : "";
  const phoneQuery = isPhoneQuery(searchParams);
  const settled = Boolean(query) && answer.query === searchParams;
  const inFlight = Boolean(query) && inFlightQuery === searchParams;
  const shown = !query
    ? []
    : settled
    ? answer.list
    : phoneQuery
    ? answer.list.filter((c) => pickMatchedPhone(c?.phoneNumberIds, searchParams))
    : [];
  const isLoading = Boolean(query) && (!settled || inFlight);

  const retry = () => {
    if (!searchParams?.trim()) return;
    runSearch(searchParams);
    elRef.current?.focus();
  };

  /* Keyed on the answer and the text, NOT on `shown` — that is a new array
     every render and would wipe the arrow-key position on each one. */
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [answer, searchParams]);

  /*
   * `stayActive` truthy = close the results but LEAVE FOCUS ALONE.
   *
   * Note `onBlur={blurResults}` passes React's event object, which is truthy —
   * so the blur path has always been stay-active by accident. The only callers
   * that genuinely want focus dropped are Escape and picking a result.
   */
  const blurResults = (stayActive) => {
    if (anchorEl === null) {
      return;
    }

    let timeout = setTimeout(() => {
      setAnchorEl(null);
      setHighlightedIndex(-1);
      if (!stayActive) {
        document.activeElement.blur();
      }
    }, 300);
    return () => {
      clearTimeout(timeout);
    };
  };

  const navUser = (result) => {
    if (returnResults) {
      returnResults(result);
      return;
    }

    navigate(`/bdr-office/clients/view-client/${result?._id}`);
  };

  /*
   * Report upward in `onResults` mode. After a FAILED search the host is told
   * [] — its Enter opens the first row it was given, which would otherwise be
   * a client from the previous search.
   */
  useEffect(() => {
    if (!onResults) return;
    onResults(settled && answer.failed ? [] : shown, {
      query,
      isNumber: phoneQuery,
      settled,
      failed: settled && answer.failed,
      inFlight,
      retry,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answer, searchParams, inFlightQuery, onResults]);

  const handleKeyDown = (event) => {
    /* The host draws the results, so the host drives them. */
    if (onResults) return;

    const isOpen = Boolean(anchorEl) && shown.length > 0 && (settled || phoneQuery);
    if (!isOpen) return;

    const count = shown.length;

    /*
     * THIS LIST OWNS THE KEYS WHILE IT IS OPEN.
     *
     * When hosted inside a BdrMenu (the Clients nav menu), the menu also binds
     * arrow/Enter handling. BdrMenu's own note warns a caller off BLANKET-
     * stopping keydown on a search field, because its intended pairing is a
     * search whose results ARE the menu's items — stop everything and its list
     * becomes mouse-only.
     *
     * This is not that pairing: this component renders its OWN result list, so
     * unstopped keys drove both at once. Arrowing moved the results AND the
     * menu's highlight, and Enter picked a client AND committed whatever the
     * menu had highlighted — which is how choosing a search result also opened
     * the New Client modal on top of it.
     *
     * Conditional, not blanket: the handler has already early-returned above
     * when this list is closed, so the menu keeps full keyboard control
     * whenever the search is not showing results.
     */
    event.stopPropagation();

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlightedIndex((prev) => {
          const next = prev < count - 1 ? prev + 1 : 0;
          scrollToItem(next);
          return next;
        });
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex((prev) => {
          const next = prev > 0 ? prev - 1 : count - 1;
          scrollToItem(next);
          return next;
        });
        break;
      case "Enter":
        event.preventDefault();
        /*
         * Falls back to the FIRST result when nothing is highlighted.
         *
         * Before, Enter did nothing at all unless you had arrowed down first —
         * typing a name and pressing Enter was a no-op. That went unnoticed
         * while this sat inside a menu, because the menu's own Enter fired
         * instead and committed whatever IT had highlighted; the key appeared
         * to work, just on the wrong thing.
         */
        if (count > 0) {
          const pick = highlightedIndex >= 0 && highlightedIndex < count
            ? highlightedIndex
            : 0;
          navUser(shown[pick]);
        }
        break;
      case "Escape":
        event.preventDefault();
        /*
         * Stop here, exactly as BdrMenu does for the same reason: a results
         * list opened inside a modal must not close the MODAL as well.
         *
         * Several hosts ARE modals — TaskModal, CreateEventModal,
         * UserTaskCreate, AddEditPayrollAdvanceModal — and each binds its own
         * `document` keydown listener. Without this, one Escape dismissed the
         * list AND tore down the form around it.
         *
         * The page hosts (Clients, MessagesPage) are unaffected either way:
         * with nothing above listening, consuming the key costs nothing.
         *
         * Stopped for every key above, not just this one — see the note at
         * the top of the switch. Escape with no results still reaches the host
         * and closes it, because the handler early-returns before that point.
         */
        blurResults();
        break;
      default:
        break;
    }
  };

  const scrollToItem = (index) => {
    if (!listRef.current) return;
    const items = listRef.current.querySelectorAll(".client-search-result-item");
    if (items[index]) {
      items[index].scrollIntoView({ block: "nearest" });
    }
  };

  return menuReturn?.isMenu ? (
    /*
     * The MENU variant: the same assign flow that opened the contact menu,
     * continued. It is a BdrMenu so the two halves are one surface — you used
     * to click a dark menu and land in a white one.
     *
     * The search box goes in `children` and the results in `items`, so typing
     * and picking share a panel and the arrow keys still drive the list.
     */
    <BdrMenu
      anchorEl={menuReturn?.anchor}
      ariaLabel="Search clients"
      className="client-search-menu"
      onClose={() => {
        menuReturn?.menuClose();
      }}
      items={[
        /* Rows only once THIS text's answer is in (nothing while it is still
           being searched), labelled with the email — as they always were. */
        ...(settled
          ? shown.length > 0
            ? shown.map((item, index) => ({
                key: `client-popover-item-${item?._id}-${index}`,
                label: `${item?.firstName} ${item?.lastName} - ${buildEmailSecondary(item)}`,
                onClick: () => navUser(item),
              }))
            : [{ header: true, label: "No results" }]
          : []),
        { divider: true },
        {
          /* keepOpen: `searchClose` only turns the search off, which drops the
             caller back to the menu it came from. Closing here as well would
             clear the anchor and take that menu with it. */
          keepOpen: true,
          label: "Cancel",
          onClick: menuReturn?.searchClose,
        },
      ]}
    >
      <BdrInput
        /* No `bdr-field--on-light` any more: this menu is dark now, so the
           standard field is the right one. That modifier exists for fields on
           a WHITE surface, and after this change the quote letterhead is its
           only remaining user. */
        className="client-search-field"
        type="text"
        label="Search Clients"
        name="clientSearch"
        autoComplete="off"
        autoFocus
        /* Deliberately NOT stopping keydown. The arrow keys and Enter have to
           reach the menu so the results are navigable while the caret stays
           here — blanket-stopping made the list mouse-only. BdrMenu ignores
           space when the event comes from a text field, which is the only key
           the two of them both want. */
        onChange={(event) => {
          setSearchParams(capitalizeFirstLetters(event?.target?.value));
        }}
        value={searchParams}
        /* The field's own spinner is the loading signal. There used to be a
           second one on a row of its own below — two spinners for one
           request. */
        loading={isLoading}
      />
    </BdrMenu>
  ) : (
    <div
      className="client-search-container"
      style={{ width: setWidth ? setWidth : "100%" }}
    >
      <BdrInput
        className="client-search-field"
        type="text"
        label="Search Clients"
        name="clientSearch"
        autoComplete="off"
        autoFocus={autoFocus}
        onBlur={blurResults}
        onFocus={(event) => {
          if (event?.target?.value?.length > 0) {
            setAnchorEl(elRef.current);
            return;
          }

          /*
           * `true` = STAY ACTIVE. Focusing a field must never blur it.
           *
           * Bare `blurResults()` leaves `stayActive` falsy, and 300ms later it
           * runs `document.activeElement.blur()` — on the very field that just
           * received focus. On a phone that read as "the search box will not
           * take focus": the tap landed, the keyboard started to open, and the
           * timer pulled focus straight back out.
           *
           * Empty field still means no results list, which is all this call was
           * ever for.
           */
          blurResults(true);
        }}
        onKeyDown={handleKeyDown}
        onChange={(event) => {
          const value = event?.target?.value;
          const trim = value?.trim() || "";
          const startsWithNumbers = /[0-9]/.test(trim?.split("")[0]);

          /* Digits get the dashes as they are typed; the on-screen filter of
             the last answer is derived (`shown`), not written here. */
          setSearchParams(
            startsWithNumbers
              ? phoneNumberFormat(value)
              : capitalizeFirstLetters(value)
          );
          if (value?.length > 0) {
            setAnchorEl(elRef.current);
            return;
          }

          blurResults(true);
        }}
        value={searchParams}
        loading={isLoading}
        /* inputRef, not ref: BdrInput is not a forwardRef, so `ref` would
           silently be null and the results popper would lose its anchor.
           This anchors it to the input itself, which is what it looked
           anchored to anyway. */
        inputRef={elRef}
      />

      {/* Results popover listing.
          DO NOT PORTAL THIS. It was briefly made portal-able so it could escape
          a modal's `overflow: hidden`, and that broke it: the list's width comes
          from `.client-search-over-div`'s `min-width: 70%` plus DESCENDANT-scoped
          overrides (e.g. `.manager-link-container .client-search-over-div`), and
          moving the node to <body> stops every one of those matching — the list
          collapsed to content width and sat on top of its own input. A modal
          that hosts this needs enough BODY HEIGHT for the list instead; see
          `.scc-manager-modal` and UserTaskCreate, which both do it that way. */}
      {/* No list of our own when the host is drawing them (`onResults`). */}
      <Popper
        placement="bottom-start"
        disablePortal={true}
        open={!onResults && Boolean(anchorEl)}
        anchorEl={anchorEl}
        sx={{ zIndex: 100 }}
        modifiers={[
          {
            name: "flip",
            enabled: true,
            options: {
              altBoundary: true,
              rootBoundary: "viewport",
              padding: 8,
            },
          },
          {
            name: "preventOverflow",
            enabled: true,
            options: {
              altAxis: true,
              altBoundary: true,
              tether: true,
              rootBoundary: "viewport",
              padding: 8,
            },
          },
          {
            name: "arrow",
            enabled: true,
            options: {
              element: anchorEl,
            },
          },
        ]}
      >
        {(settled || phoneQuery) && shown.length > 0 ? (
          <div className="client-search-over-div" ref={listRef}>
            {shown.map((item, index) => {
              // A number search shows the number it matched on, the matched
              // part bold; anything else (or a row it no longer matches) shows
              // the email line.
              const match = phoneQuery
                ? pickMatchedPhone(item?.phoneNumberIds, searchParams)
                : null;

              const secondaryDisplay = match ? (
                <span className="client-search-number-split">
                  <strong>{match.matched}</strong>
                  <span>{match.rest}</span>
                </span>
              ) : (
                buildEmailSecondary(item)
              );

              const managerHint = item?.manager?._id ? (
                <div className="client-search-manager-hint">
                  {`Already managed by: ${item.manager.firstName || ""} ${
                    item.manager.lastName || ""
                  }`.trim()}
                </div>
              ) : null;

              return (
                <button
                  key={`client-popover-item-${item?._id}`}
                  className={`client-search-result-item${
                    index === highlightedIndex ? " highlighted" : ""
                  }`}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    navUser(item);
                  }}
                >
                  <div>{`${item?.firstName} ${item?.lastName} - `}</div>
                  <div>{secondaryDisplay}</div>
                  {managerHint}
                </button>
              );
            })}
          </div>
        ) : (
          <>
            {settled ? (
              <div className="client-search-over-div">
                <h5>No Results</h5>
              </div>
            ) : (
              <>
                {phoneQuery && (
                  <div
                    className="client-search-over-div"
                    style={{
                      alignItems: "center",
                      padding: "8px",
                    }}
                  >
                    <span className="bdr-input-spinner" role="status" aria-label="Searching" />
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Popper>
    </div>
  );
};

export default ClientSearchInput;
